import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { UIEvent } from "react";
import type { DataStudioProfileRow } from "../../../services/dataStudio/dataStudioTables.types";

export const PROFILE_ROW_BATCH_SIZE = 12;
const LOAD_MORE_THRESHOLD = 64;

export const useDataStudioProfileTableInternalState = (
  tableId: string,
  rows: DataStudioProfileRow[],
  hasMoreApiRows = false,
  isLoadingMoreRows = false,
  onLoadMoreRows?: () => void,
  onColumnMenu?: (columnId: string, actionId: string) => void,
  initialImportantColumnIds: string[] = [],
  onColumnImportanceChange?: (columnId: string, isImportant: boolean) => void,
  hasOlderApiRows = false,
  isLoadingOlderRows = false,
  onLoadOlderRows?: () => void,
  hasMoreApiColumns = false,
  isLoadingMoreColumns = false,
  onLoadMoreColumns?: () => void,
  hasOlderApiColumns = false,
  isLoadingOlderColumns = false,
  onLoadOlderColumns?: () => void,
) => {
  const [visibleRowCount, setVisibleRowCount] = useState(
    Math.min(PROFILE_ROW_BATCH_SIZE, rows.length),
  );
  const [detailsColumnId, setDetailsColumnId] = useState<string | null>(null);
  const [importantColumnIds, setImportantColumnIds] = useState<Set<string>>(
    () => new Set(initialImportantColumnIds),
  );
  /**
   * Actual horizontal scroll position, not just the API's paging flags —
   * `hasOlderApiColumns`/`hasMoreApiColumns` only say whether a page beyond
   * what's cached exists, which stays `false` for the whole time a user
   * scrolls through the columns already cached (nothing has been evicted
   * yet). Gating the edge banners on "currently scrolled to that edge" too
   * is what stops "You're at the first column" from appearing to stick while
   * the user is actually several pages in.
   */
  const [isAtColumnsStart, setIsAtColumnsStart] = useState(true);
  const [isAtColumnsEnd, setIsAtColumnsEnd] = useState(false);
  const previousTableId = useRef(tableId);

  /**
   * Scroll-anchoring bookkeeping for the older-rows (top) direction: the
   * scroll container itself (captured off the scroll event — the viewport
   * component doesn't forward a ref), the rows length at the moment a fetch
   * was requested (to size how many rows a response prepended), and the
   * scroll height just before that response lands (to know how much height
   * was added above the viewport so `scrollTop` can be shifted by the same
   * amount and the visible content doesn't jump).
   */
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  /**
   * Captured the same way as `scrollContainerRef` above — off the scroll
   * event, since the horizontal scroller (`ProfileScroller`) doesn't forward
   * a ref either — so the table-switch effect below has a real DOM node to
   * zero `scrollLeft` on instead of leaving the browser's native scroll
   * position (which survives a re-render of the same, unkeyed node) pointing
   * at whatever column the previous table was scrolled to.
   */
  const horizontalScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const isAwaitingOlderRowsRef = useRef(false);
  const rowsLengthBeforeOlderFetchRef = useRef(rows.length);
  const scrollHeightBeforeOlderFetchRef = useRef(0);
  const pendingScrollAdjustmentRef = useRef(false);

  useEffect(() => {
    const tableChanged = previousTableId.current !== tableId;
    previousTableId.current = tableId;
    if (tableChanged) {
      setDetailsColumnId(null);
      setImportantColumnIds(new Set(initialImportantColumnIds));
      setIsAtColumnsStart(true);
      setIsAtColumnsEnd(false);
      isAwaitingOlderRowsRef.current = false;
      pendingScrollAdjustmentRef.current = false;
    }
    setVisibleRowCount((currentCount) => {
      if (tableChanged) {
        return Math.min(PROFILE_ROW_BATCH_SIZE, rows.length);
      }
      // The rows query can resolve after the profile/columns query for the
      // same table, so `rows` sometimes goes from empty to populated without
      // `tableId` ever changing — grow back up to the first batch in that
      // case instead of staying clamped at whatever `currentCount` was when
      // the grid first mounted with no rows.
      const initialBatch = Math.min(PROFILE_ROW_BATCH_SIZE, rows.length);
      return Math.min(Math.max(currentCount, initialBatch), rows.length);
    });
  }, [initialImportantColumnIds, rows.length, tableId]);

  /**
   * Neither scroll container is keyed by `tableId`, so switching tables
   * reuses the same DOM nodes and the browser keeps their native
   * `scrollTop`/`scrollLeft` — without this, a table opened already scrolled
   * to (say) columns 10-15 stays visually scrolled there after switching to
   * a different table, even though the state above and the underlying query
   * have both already reset. `useLayoutEffect` so the reset lands before
   * paint instead of flashing the stale position for a frame.
   */
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    if (horizontalScrollContainerRef.current) {
      horizontalScrollContainerRef.current.scrollLeft = 0;
    }
  }, [tableId]);

  // Detect a response prepending older rows and grow the visible window by
  // the same amount so nothing already on screen is pushed out of view.
  useLayoutEffect(() => {
    if (!isAwaitingOlderRowsRef.current) {
      rowsLengthBeforeOlderFetchRef.current = rows.length;
      return;
    }
    isAwaitingOlderRowsRef.current = false;
    const addedCount = rows.length - rowsLengthBeforeOlderFetchRef.current;
    rowsLengthBeforeOlderFetchRef.current = rows.length;
    if (addedCount > 0) {
      setVisibleRowCount((currentCount) => currentCount + addedCount);
      pendingScrollAdjustmentRef.current = true;
    }
  }, [rows.length]);

  // Runs after the prepended rows have actually mounted (visibleRowCount's
  // own commit) — only then does scrollHeight reflect their height, so the
  // compensating scrollTop shift lands in the same paint instead of flickering.
  useLayoutEffect(() => {
    if (pendingScrollAdjustmentRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const addedHeight =
        container.scrollHeight - scrollHeightBeforeOlderFetchRef.current;
      if (addedHeight > 0) {
        container.scrollTop += addedHeight;
      }
    }
    pendingScrollAdjustmentRef.current = false;
  }, [visibleRowCount]);

  const visibleRows = useMemo(
    () => rows.slice(0, visibleRowCount),
    [rows, visibleRowCount],
  );

  const handleViewportScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      scrollContainerRef.current = event.currentTarget;

      const isNearBottom =
        scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD;
      const isNearTop = scrollTop <= LOAD_MORE_THRESHOLD;

      if (isNearBottom) {
        if (visibleRowCount < rows.length) {
          setVisibleRowCount((currentCount) =>
            Math.min(currentCount + PROFILE_ROW_BATCH_SIZE, rows.length),
          );
        } else if (hasMoreApiRows && !isLoadingMoreRows) {
          onLoadMoreRows?.();
        }
      }

      else if (isNearTop && hasOlderApiRows && !isLoadingOlderRows) {
        isAwaitingOlderRowsRef.current = true;
        rowsLengthBeforeOlderFetchRef.current = rows.length;
        scrollHeightBeforeOlderFetchRef.current = scrollHeight;
        onLoadOlderRows?.();
      }
    },
    [
      hasMoreApiRows,
      hasOlderApiRows,
      isLoadingMoreRows,
      isLoadingOlderRows,
      onLoadMoreRows,
      onLoadOlderRows,
      rows.length,
      visibleRowCount,
    ],
  );

  /**
   * Horizontal counterpart of `handleViewportScroll`, attached to the
   * scroller that owns `overflow-x` (`ProfileScroller`) rather than the rows
   * viewport that owns `overflow-y` — the two directions scroll different DOM
   * elements, so they need their own handler. Columns render directly off
   * `columns` with no client-side reveal window (unlike rows' `visibleRowCount`
   * batching), so nearing either edge forwards straight to the API callbacks.
   */
  const handleGridScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { clientWidth, scrollWidth, scrollLeft } = event.currentTarget;
      horizontalScrollContainerRef.current = event.currentTarget;

      const isNearRight =
        scrollWidth - scrollLeft - clientWidth <= LOAD_MORE_THRESHOLD;
      const isNearLeft = scrollLeft <= LOAD_MORE_THRESHOLD;

      setIsAtColumnsStart(isNearLeft);
      setIsAtColumnsEnd(isNearRight);

      if (isNearRight && hasMoreApiColumns && !isLoadingMoreColumns) {
        onLoadMoreColumns?.();
      }

      else if (isNearLeft && hasOlderApiColumns && !isLoadingOlderColumns) {
        onLoadOlderColumns?.();
      }
    },
    [
      hasMoreApiColumns,
      hasOlderApiColumns,
      isLoadingMoreColumns,
      isLoadingOlderColumns,
      onLoadMoreColumns,
      onLoadOlderColumns,
    ],
  );

  const handleColumnMenu = useCallback(
    (columnId: string, actionId: string) => {
      if (actionId === "viewColumnDetails") {
        setDetailsColumnId(columnId);
      }
      if (
        actionId === "markAsImportant" &&
        !importantColumnIds.has(columnId)
      ) {
        setImportantColumnIds((current) => new Set(current).add(columnId));
        onColumnImportanceChange?.(columnId, true);
      }
      if (
        actionId === "unmarkAsImportant" &&
        importantColumnIds.has(columnId)
      ) {
        setImportantColumnIds((current) => {
          const next = new Set(current);
          next.delete(columnId);
          return next;
        });
        onColumnImportanceChange?.(columnId, false);
      }
      onColumnMenu?.(columnId, actionId);
    },
    [importantColumnIds, onColumnImportanceChange, onColumnMenu],
  );

  const closeColumnDetails = useCallback(() => {
    setDetailsColumnId(null);
  }, []);

  return {
    visibleRows,
    visibleRowCount,
    hasMoreRows: visibleRowCount < rows.length || hasMoreApiRows,
    handleViewportScroll,
    handleGridScroll,
    detailsColumnId,
    importantColumnIds,
    handleColumnMenu,
    closeColumnDetails,
    isAtColumnsStart,
    isAtColumnsEnd,
  };
};
